import { Box, Button, Typography, CssBaseline } from "@mui/material";
import { FC } from "react";
import { useMsal } from "@azure/msal-react";

interface AccessDeniedProps {
  message?: string;
}

/**
 * AccessDenied component displayed when a user is authenticated via Azure AD
 * but is not authorized to use the application (e.g., not in the database).
 */
const AccessDenied: FC<AccessDeniedProps> = ({ message }) => {
  const { instance } = useMsal();

  const getLanguage = () =>
    window.localStorage.i18nextLng || navigator.language || "en";

  const isGerman = getLanguage().startsWith("de");

  const handleLogout = () => {
    instance.logoutRedirect({
      account: instance.getActiveAccount() ?? undefined,
    });
  };

  return (
    <Box>
      <CssBaseline />
      <Box
        component="main"
        sx={theme => ({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 3,
          backgroundColor: theme.palette.grey[900],
          overflow: "auto",
          padding: 4,

          ...theme.applyStyles("light", {
            backgroundColor: theme.palette.background.paper
          })
        })}>
        <Box
          component="img"
          sx={{
            width: "20vw",
            minWidth: 150,
            maxWidth: 300,
            marginBottom: 2,
          }}
          alt="Pinnacle logo"
          src="/logo.svg"
        />

        <Typography
          variant="h4"
          color="error"
          sx={{
            textAlign: "center",
            fontWeight: 600
          }}>
          {isGerman ? "Zugriff verweigert" : "Access Denied"}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            textAlign: "center",
            maxWidth: 500
          }}>
          {message ||
            (isGerman
              ? "Sie sind nicht berechtigt, auf diese Anwendung zuzugreifen. Bitte wenden Sie sich an Ihren Administrator."
              : "You are not authorized to access this application. Please contact your administrator.")}
        </Typography>

        <Button
          variant="contained"
          onClick={handleLogout}
          sx={{ marginTop: 2, minWidth: 200 }}
        >
          {isGerman ? "Abmelden" : "Sign Out"}
        </Button>
      </Box>
    </Box>
  );
};

export default AccessDenied;
